import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import './ProductGrid.css';
import ProductCard from './ProductCard';
import InfiniteScrollLoader from './InfiniteScrollLoader';
import { useData } from '../context/DataContext';
import { sanitizeUserFacingError } from '../utils/userFacingError';
import { idsMatch, productMatchesSelectedCategory } from '../utils/catalogIds';
import { API_BASE_URL } from '../utils/api';
import { shapeProductForStorefront } from '../utils/catalogProductShape';
import { getProductDisplayPrice } from '../utils/filterPrice';
import { groupBrowsableProductsForGrid, sortGroupedProducts } from '../utils/productOffers';
import { trackSearchNoResults, trackPriceMatchFilterToggle } from '../utils/analyticsTracker';
import { countVisiblePriceMatchProducts, productHasVisiblePriceMatch } from '../utils/hotpointPriceMatch';
import { useStorefrontSettings } from '../context/StorefrontSettingsContext';
import PriceMatchChipBar from './PriceMatchChipBar';

const productImages = {
  electronics: '/images/products/electronics.jpg',
  fashion: '/images/products/fashion.jpg',
  home: '/images/products/home.jpg',
  sports: '/images/products/sports.jpg',
  beauty: '/images/products/beauty.jpg',
  toys: '/images/products/toys.jpg',
  diy: '/images/products/diy.jpg',
  gaming: '/images/products/gaming.jpg',
  'data-storage': '/images/products/data-storage.jpg',
  'repair-tools': '/images/products/repair-tools.jpg'
};

const getProductImage = (category) => productImages[category] || productImages.electronics;

function normalizeTextForCompare(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Map UI sort to API `sortBy` (backend sorts the full filtered catalog). */
function toApiSortBy(sortBy) {
  if (
    sortBy === 'price-low' ||
    sortBy === 'price-high' ||
    sortBy === 'discount' ||
    sortBy === 'popular'
  ) {
    return sortBy;
  }
  return '';
}

const SERVER_PAGE_SIZE = 48;
const SERVER_MAX_PAGES = 40;

const ProductGrid = ({
  filters,
  onFilterChange,
  showFullWidth = false,
  onAddToList,
  onAddToCart,
  onAddToCompare,
  onCheckAvailability,
  onRemoveFromList,
  listProductIds,
  compareProductIds,
  hideHeaderTitle = false,
  layoutVariant,
  /** Category pages: scroll-only loading (no Show More button). */
  infiniteScrollOnly = false,
}) => {
  const handleAddToList = onAddToList || onAddToCart;
  const {
    products,
    categories,
    subcategoriesByCategory,
    loading,
    error,
    initialCatalogLoaded,
    getPromotionalProducts,
    getGeekProducts,
    getAvailableProducts
  } = useData();
  const { showPriceMatch } = useStorefrontSettings();
  const [sortBy, setSortBy] = useState('popular');
  const [displayCount, setDisplayCount] = useState(12);
  const [serverFilteredProducts, setServerFilteredProducts] = useState(null);
  const [serverFiltering, setServerFiltering] = useState(false);
  const [serverLoadingMore, setServerLoadingMore] = useState(false);
  const [serverNextPage, setServerNextPage] = useState(1);
  const [serverHasMore, setServerHasMore] = useState(false);
  /** Bumped on effect cleanup so stale async work never skips `setServerFiltering(false)` (StrictMode / fast re-filters). */
  const serverFetchGenRef = useRef(0);
  // Keep latest map for query building without re-running the server-fetch effect when the
  // context first populates `{}` → full map (that aborts in-flight fetches and feels like a double-click).
  const subcategoriesByCategoryRef = useRef(subcategoriesByCategory);
  subcategoriesByCategoryRef.current = subcategoriesByCategory;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;
  const serverNextPageRef = useRef(1);
  const serverHasMoreRef = useRef(false);
  const serverProductsRef = useRef([]);

  const serverFilterActive = Boolean(
    (filters.category && filters.category !== 'all') ||
      (filters.subCategory && filters.subCategory !== 'all') ||
      (filters.store && filters.store !== 'all') ||
      String(filters.searchQuery || '').trim().length >= 2
  );

  const extractProductsFromApiResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.products)) return payload.products;
    if (payload && payload.data && Array.isArray(payload.data.products)) return payload.data.products;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const shapeServerRows = (raw) => {
    const shaped = [];
    for (const p of raw) {
      try {
        shaped.push(shapeProductForStorefront(p));
      } catch {
        const idVal = p._id || p.id;
        shaped.push({ ...p, id: idVal, _id: idVal });
      }
    }
    return shaped;
  };

  const buildServerQuery = useCallback(({ includeCategory, page, sortValue }) => {
    const f = filtersRef.current;
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('limit', String(SERVER_PAGE_SIZE));
    qs.set('view', 'card');
    qs.set('includeInactive', 'false');

    const apiSort = toApiSortBy(sortValue);
    if (apiSort) qs.set('sortBy', apiSort);

    const searchTerm = String(f.searchQuery || '').trim();
    if (searchTerm.length >= 2) {
      qs.set('search', searchTerm);
    }

    if (includeCategory && f.category && f.category !== 'all') {
      qs.set('category', String(f.category));
    }
    if (f.store && f.store !== 'all') {
      qs.set('storeId', String(f.store));
    }
    if (f.subCategory && f.subCategory !== 'all') {
      const ids = String(f.subCategory)
        .split('::')
        .map((v) => v.trim())
        .filter(Boolean);

      const subByAnyId = new Map();
      Object.values(subcategoriesByCategoryRef.current || {}).forEach((subs) => {
        (subs || []).forEach((sub) => {
          if (!sub) return;
          const name = String(sub.name || '').trim();
          const keys = [sub.id, sub._id, sub.railwayId]
            .map((v) => (v != null ? String(v).trim() : ''))
            .filter(Boolean);
          keys.forEach((k) => subByAnyId.set(k.toLowerCase(), { ...sub, name }));
        });
      });
      const resolvedSubs = ids
        .map((idVal) => subByAnyId.get(String(idVal).toLowerCase()))
        .filter(Boolean);
      const resolved = resolvedSubs[0] || null;
      const mongoId = ids[0] || String(resolved?._id || resolved?.id || '').trim();
      const railwayId = ids[1] || String(resolved?.railwayId || '').trim();
      const name = String(f.subCategoryName || resolved?.name || '').trim();

      if (mongoId) qs.set('subCategory', mongoId);
      if (railwayId) qs.set('subCategoryId', railwayId);
      if (name) qs.set('subCategoryName', name);
    }
    return qs;
  }, []);

  useEffect(() => {
    if (!serverFilterActive) {
      setServerFilteredProducts(null);
      setServerFiltering(false);
      setServerLoadingMore(false);
      setServerNextPage(1);
      setServerHasMore(false);
      serverNextPageRef.current = 1;
      serverHasMoreRef.current = false;
      serverProductsRef.current = [];
      return undefined;
    }
    const apiRoot =
      API_BASE_URL ||
      (() => {
        const raw = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
        if (!raw) return '';
        return /\/api$/i.test(raw) ? raw : `${raw}/api`;
      })();
    if (!apiRoot) {
      setServerFilteredProducts(null);
      setServerFiltering(false);
      return undefined;
    }

    const gen = ++serverFetchGenRef.current;
    const isCurrent = () => gen === serverFetchGenRef.current;
    const endpoints = ['/products', '/catalog/products'];
    const sortValue = sortByRef.current;

    const tryLoadPage = async (query) => {
      let loaded = null;
      let totalPages = null;
      for (const ep of endpoints) {
        try {
          const res = await fetch(`${apiRoot}${ep}?${query.toString()}`);
          if (!res.ok) continue;
          const json = await res.json();
          loaded = extractProductsFromApiResponse(json);
          const pagesHint = json?.pagination?.pages ?? json?.data?.pagination?.pages;
          if (pagesHint != null) totalPages = Number(pagesHint) || null;
          if (!Array.isArray(loaded)) continue;
          if (loaded.length > 0 || ep === endpoints[endpoints.length - 1]) break;
        } catch {
          // try next endpoint
        }
      }
      return {
        products: Array.isArray(loaded) ? loaded : [],
        totalPages,
      };
    };

    const loadPages = async (includeCategory, fromPage, pageCount) => {
      const all = [];
      const seen = new Set();
      let totalPagesHint = null;
      let lastPageLoaded = fromPage - 1;
      let exhausted = false;

      for (let i = 0; i < pageCount; i += 1) {
        const page = fromPage + i;
        if (page > SERVER_MAX_PAGES) {
          exhausted = true;
          break;
        }
        if (!isCurrent()) return { products: all, nextPage: page, exhausted: true };
        const { products: pageProducts, totalPages } = await tryLoadPage(
          buildServerQuery({ includeCategory, page, sortValue })
        );
        if (totalPages != null) totalPagesHint = totalPages;
        lastPageLoaded = page;

        pageProducts.forEach((p) => {
          const id = String(p?._id || p?.id || '');
          if (!id || seen.has(id)) return;
          seen.add(id);
          all.push(p);
        });

        const pageExhausted =
          pageProducts.length < SERVER_PAGE_SIZE ||
          (totalPagesHint != null && page >= totalPagesHint);
        if (pageExhausted) {
          exhausted = true;
          break;
        }
      }

      return {
        products: all,
        nextPage: lastPageLoaded + 1,
        exhausted,
      };
    };

    const run = async () => {
      setServerFilteredProducts(null);
      setServerFiltering(true);
      setServerLoadingMore(false);
      setServerNextPage(1);
      setServerHasMore(false);
      serverNextPageRef.current = 1;
      serverHasMoreRef.current = false;
      serverProductsRef.current = [];

      try {
        // First page(s) already sorted by the API across the full filtered catalog.
        let result = await loadPages(true, 1, 1);

        const f0 = filtersRef.current;
        if (
          isCurrent() &&
          result.products.length === 0 &&
          f0.subCategory &&
          f0.subCategory !== 'all' &&
          f0.category &&
          f0.category !== 'all'
        ) {
          result = await loadPages(false, 1, 1);
        }

        const f1 = filtersRef.current;
        if (isCurrent() && result.products.length === 0 && f1.subCategory && f1.subCategory !== 'all') {
          await new Promise((resolve) => setTimeout(resolve, 220));
          if (isCurrent()) {
            const retryStrict = await loadPages(true, 1, 1);
            if (retryStrict.products.length > 0) {
              result = retryStrict;
            } else if (f1.category && f1.category !== 'all') {
              result = await loadPages(false, 1, 1);
            }
          }
        }

        if (isCurrent()) {
          const shaped = shapeServerRows(result.products);
          serverProductsRef.current = shaped;
          serverNextPageRef.current = result.nextPage;
          serverHasMoreRef.current = !result.exhausted;
          setServerFilteredProducts(shaped);
          setServerNextPage(result.nextPage);
          setServerHasMore(!result.exhausted);
        }
      } catch {
        if (isCurrent()) {
          setServerFilteredProducts([]);
          setServerHasMore(false);
          serverHasMoreRef.current = false;
          serverProductsRef.current = [];
        }
      } finally {
        if (isCurrent()) {
          setServerFiltering(false);
        }
      }
    };
    run();
    return () => {
      serverFetchGenRef.current += 1;
    };
  }, [
    serverFilterActive,
    filters.category,
    filters.subCategory,
    filters.subCategoryName,
    filters.store,
    filters.searchQuery,
    sortBy,
    buildServerQuery,
  ]);

  const fetchMoreServerProducts = useCallback(async () => {
    if (!serverFilterActive || !serverHasMoreRef.current || serverLoadingMore) {
      return false;
    }
    const apiRoot =
      API_BASE_URL ||
      (() => {
        const raw = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
        if (!raw) return '';
        return /\/api$/i.test(raw) ? raw : `${raw}/api`;
      })();
    if (!apiRoot) return false;

    const gen = serverFetchGenRef.current;
    const isCurrent = () => gen === serverFetchGenRef.current;
    const fromPage = serverNextPageRef.current;
    const sortValue = sortByRef.current;
    const endpoints = ['/products', '/catalog/products'];

    setServerLoadingMore(true);
    try {
      let loaded = null;
      let totalPages = null;
      const query = buildServerQuery({
        includeCategory: true,
        page: fromPage,
        sortValue,
      });
      for (const ep of endpoints) {
        try {
          const res = await fetch(`${apiRoot}${ep}?${query.toString()}`);
          if (!res.ok) continue;
          const json = await res.json();
          loaded = extractProductsFromApiResponse(json);
          const pagesHint = json?.pagination?.pages ?? json?.data?.pagination?.pages;
          if (pagesHint != null) totalPages = Number(pagesHint) || null;
          if (!Array.isArray(loaded)) continue;
          if (loaded.length > 0 || ep === endpoints[endpoints.length - 1]) break;
        } catch {
          // try next
        }
      }
      if (!isCurrent()) return false;

      const pageProducts = Array.isArray(loaded) ? loaded : [];
      const seen = new Set(serverProductsRef.current.map((p) => String(p._id || p.id || '')));
      const shaped = shapeServerRows(pageProducts).filter((p) => {
        const id = String(p._id || p.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const next = [...serverProductsRef.current, ...shaped];
      serverProductsRef.current = next;
      const exhausted =
        pageProducts.length < SERVER_PAGE_SIZE ||
        fromPage >= SERVER_MAX_PAGES ||
        (totalPages != null && fromPage >= totalPages);
      serverNextPageRef.current = fromPage + 1;
      serverHasMoreRef.current = !exhausted;
      setServerFilteredProducts(next);
      setServerNextPage(fromPage + 1);
      setServerHasMore(!exhausted);
      return shaped.length > 0 || !exhausted;
    } catch {
      return false;
    } finally {
      if (isCurrent()) setServerLoadingMore(false);
    }
  }, [serverFilterActive, serverLoadingMore, buildServerQuery]);

  // Reset display count when filters or sort change
  useEffect(() => {
    setDisplayCount(12);
  }, [filters.category, filters.subCategory, filters.store, filters.brand, filters.priceRange, filters.priceMin, filters.priceMax, filters.searchQuery, filters.imageSearch, filters.showPromotions, filters.showGeek, filters.showPriceMatchOnly, sortBy]);

  const filteredAndSortedProducts = useMemo(() => {
    const sourceProducts = serverFilterActive
      ? (Array.isArray(serverFilteredProducts) ? serverFilteredProducts : [])
      : products;
    let filtered = [...sourceProducts];
    
    // Ensure products have thumbnail/primary image
    filtered = filtered.map(p => {
      // Use thumbnail if available, otherwise primary image, otherwise first image, otherwise fallback
      let thumbnail = p.thumbnail;
      if (!thumbnail && p.images && Array.isArray(p.images) && p.images.length > 0) {
        const primaryIndex = p.primaryImageIndex !== undefined && p.primaryImageIndex < p.images.length
          ? p.primaryImageIndex
          : 0;
        thumbnail = p.images[primaryIndex];
      }
      if (!thumbnail) {
        thumbnail = p.image || getProductImage(p.categorySlug || p.category);
      }
      return {
        ...p,
        thumbnail: thumbnail,
        image: thumbnail
      };
    });

    // Filter by search query when browsing client-side catalog (server search handles active queries).
    if (filters.searchQuery && !serverFilterActive) {
      const q = String(filters.searchQuery).trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(
          (p) =>
            String(p.name || '')
              .toLowerCase()
              .includes(q) ||
            String(p.description || '')
              .toLowerCase()
              .includes(q) ||
            String(p.categoryName || '')
              .toLowerCase()
              .includes(q)
        );
      }
    }

    const hasActiveTextSearch = Boolean(String(filters.searchQuery || '').trim());

    if (filters.showPromotions) {
      filtered = serverFilterActive
        ? filtered.filter((p) => (p.discount || 0) > 0)
        : getPromotionalProducts();
    }

    if (filters.showGeek) {
      filtered = serverFilterActive ? filtered.filter((p) => !!p.geek) : getGeekProducts();
    }

    const hasSubcategoryFilter =
      !serverFilterActive && Boolean(filters.subCategory && filters.subCategory !== 'all');

    // Category scope is skipped during text search (main search bar is site-wide).
    if (
      !serverFilterActive &&
      filters.category &&
      filters.category !== 'all' &&
      !hasSubcategoryFilter &&
      !hasActiveTextSearch
    ) {
      const wantId = String(filters.category).trim();
      filtered = filtered.filter((p) => productMatchesSelectedCategory(p, wantId, categories));
    }

    // Filter by subcategory (Mongo sub _id, Railway UUID, and/or display name)
    if (!serverFilterActive && filters.subCategory && filters.subCategory !== 'all' && !hasActiveTextSearch) {
      const raw = String(filters.subCategory).trim();
      const wantedIds = raw.includes('::')
        ? raw.split('::').map((v) => v.trim()).filter(Boolean)
        : [raw];
      const wantedTextTokens = wantedIds
        .map((v) => normalizeTextForCompare(v))
        .filter((v) => v && !/^[a-f\d]{24}$/i.test(v) && !/^[a-f\d-]{32,36}$/i.test(v));

      const subNameByAnyId = new Map();
      Object.values(subcategoriesByCategory || {}).forEach((subs) => {
        (subs || []).forEach((sub) => {
          if (!sub) return;
          const nm = normalizeTextForCompare(sub.name);
          if (!nm) return;
          const ids = [sub.id, sub._id, sub.railwayId]
            .map((v) => (v != null ? String(v).trim() : ''))
            .filter(Boolean);
          ids.forEach((idVal) => {
            const k = String(idVal).trim().toLowerCase();
            if (!k) return;
            if (!subNameByAnyId.has(k)) subNameByAnyId.set(k, nm);
          });
        });
      });

      const matchingSubs = [];
      const tryAddFromList = (subs) => {
        if (!Array.isArray(subs)) return;
        subs.forEach((sub) => {
          if (!sub) return;
          const hit = wantedIds.some(
            (want) =>
              idsMatch(sub.id, want) || (sub.railwayId != null && idsMatch(sub.railwayId, want))
          );
          if (hit) matchingSubs.push(sub);
        });
      };
      if (filters.category && filters.category !== 'all') {
        tryAddFromList(subcategoriesByCategory?.[filters.category]);
      }
      if (matchingSubs.length === 0) {
        Object.entries(subcategoriesByCategory || {}).forEach(([catKey, subs]) => {
          if (filters.category && filters.category !== 'all' && !idsMatch(catKey, filters.category)) {
            return;
          }
          tryAddFromList(subs);
        });
      }
      const wantNames = [
        ...new Set(
          matchingSubs
            .map((s) => normalizeTextForCompare(s.name))
            .filter(Boolean)
        )
      ];
      const explicitName = normalizeTextForCompare(filters.subCategoryName);
      if (explicitName && !wantNames.includes(explicitName)) {
        wantNames.push(explicitName);
      }
      wantedTextTokens.forEach((t) => {
        if (!wantNames.includes(t)) wantNames.push(t);
      });

      filtered = filtered.filter((p) => {
        const pSubObj = typeof p.subCategory === 'object' && p.subCategory ? p.subCategory : null;
        const candidateIds = [
          p.subCategory,
          p.subCategoryId,
          pSubObj?._id,
          pSubObj?.id,
          pSubObj?.railwayId
        ]
          .map((v) => (v != null ? String(v).trim() : ''))
          .filter(Boolean);

        const idHit = wantedIds.some((want) => candidateIds.some((candidate) => idsMatch(candidate, want)));
        if (idHit) return true;
        if (wantNames.length === 0) return false;

        const candidateNames = [
          p.subCategoryName,
          pSubObj?.name,
          p.subcategoryName,
          // Some sync rows may store display name directly in `subCategory` string.
          typeof p.subCategory === 'string' ? p.subCategory : '',
          // Resolve name from known subcategory ids when product row stores only ids.
          ...candidateIds.map((idVal) => subNameByAnyId.get(String(idVal).toLowerCase()) || '')
        ]
          .map((v) => normalizeTextForCompare(v))
          .filter(Boolean);

        return wantNames.some((n) => candidateNames.includes(n));
      });
    }

    if (!serverFilterActive && filters.store && filters.store !== 'all') {
      const wantStore = String(filters.store).trim();
      filtered = filtered.filter((p) => {
        const candidateStores = [
          p.storeId,
          p.store?._id,
          p.store?.id,
          p.vendor?._id,
          p.vendor?.id,
        ]
          .map((v) => (v != null ? String(v).trim() : ''))
          .filter(Boolean);
        return candidateStores.some((id) => idsMatch(id, wantStore));
      });
    }

    if (!serverFilterActive && filters.brand && filters.brand !== 'all') {
      const wantBrand = String(filters.brand).trim().toLowerCase();
      filtered = filtered.filter(
        (p) => String(p.brand || '').trim().toLowerCase() === wantBrand
      );
    }

    // Price slider (Ksh) — use display/sale price for every product row
    if (filters.priceMax != null) {
      const min = filters.priceMin ?? 0;
      const max = filters.priceMax;
      filtered = filtered.filter((p) => {
        const price = getProductDisplayPrice(p);
        return price != null && price >= min && price <= max;
      });
    } else if (filters.priceRange !== 'all') {
      let min, max;
      if (filters.priceRange === '200+') {
        min = 20000;
        max = Infinity;
      } else {
        const [minStr, maxStr] = filters.priceRange.split('-');
        min = parseInt(minStr, 10) * 100;
        max = maxStr ? parseInt(maxStr, 10) * 100 : Infinity;
      }
      filtered = filtered.filter((p) => p.price >= min && p.price <= max);
    }
    
    // Only show available products
    filtered = getAvailableProducts(filtered);

    return filtered;
  }, [
    filters,
    products,
    categories,
    subcategoriesByCategory,
    getPromotionalProducts,
    getGeekProducts,
    getAvailableProducts,
    serverFilterActive,
    serverFilteredProducts
  ]);

  const priceMatchCount = useMemo(
    () => countVisiblePriceMatchProducts(getAvailableProducts(filteredAndSortedProducts), showPriceMatch),
    [filteredAndSortedProducts, getAvailableProducts, showPriceMatch]
  );

  const productsForGrid = useMemo(() => {
    if (!filters.showPriceMatchOnly) return filteredAndSortedProducts;
    return filteredAndSortedProducts.filter((product) =>
      productHasVisiblePriceMatch(product, showPriceMatch)
    );
  }, [filteredAndSortedProducts, filters.showPriceMatchOnly, showPriceMatch]);

  const groupedDisplayProducts = useMemo(() => {
    const browsable = getAvailableProducts(productsForGrid);
    const catalog = getAvailableProducts(products);
    const grouped = groupBrowsableProductsForGrid(browsable, catalog);
    return sortGroupedProducts(grouped, sortBy);
  }, [productsForGrid, products, sortBy, getAvailableProducts]);

  const displayedProducts = groupedDisplayProducts.slice(0, displayCount);
  const hasMore =
    groupedDisplayProducts.length > displayCount ||
    (serverFilterActive && serverHasMore);
  const serverDataPending = serverFilterActive && !Array.isArray(serverFilteredProducts);
  const lastSearchGapRef = useRef('');

  useEffect(() => {
    const query = String(filters.searchQuery || '').trim();
    if (!query || loading || serverDataPending) return;
    const resultCount = groupedDisplayProducts.length;
    const trackKey = `${query.toLowerCase()}:${resultCount}`;
    if (resultCount === 0 && lastSearchGapRef.current !== trackKey) {
      trackSearchNoResults(query, 0);
      lastSearchGapRef.current = trackKey;
    } else if (lastSearchGapRef.current !== trackKey) {
      lastSearchGapRef.current = trackKey;
    }
  }, [filters.searchQuery, groupedDisplayProducts.length, loading, serverDataPending]);

  const loadMoreBatchSize = 12;

  const handleShowMore = useCallback(async () => {
    const nextCount = displayCount + loadMoreBatchSize;
    if (
      serverFilterActive &&
      serverHasMoreRef.current &&
      nextCount > groupedDisplayProducts.length
    ) {
      await fetchMoreServerProducts();
    }
    setDisplayCount((prev) => prev + loadMoreBatchSize);
  }, [
    displayCount,
    loadMoreBatchSize,
    serverFilterActive,
    groupedDisplayProducts.length,
    fetchMoreServerProducts,
  ]);

  const infiniteScrollResetKey = [
    filters.category,
    filters.subCategory,
    filters.store,
    filters.brand,
    filters.priceRange,
    filters.priceMin,
    filters.priceMax,
    filters.searchQuery,
    filters.imageSearch,
    filters.showPromotions,
    filters.showGeek,
    filters.showPriceMatchOnly,
    sortBy,
  ].join('|');

  const handlePriceMatchChipToggle = useCallback(() => {
    const nextActive = !filters.showPriceMatchOnly;
    trackPriceMatchFilterToggle(nextActive, priceMatchCount, { source: 'product_grid' });
    onFilterChange({ showPriceMatchOnly: nextActive });
  }, [filters.showPriceMatchOnly, onFilterChange, priceMatchCount]);

  if (!initialCatalogLoaded && products.length === 0) {
    return (
      <div className={`product-grid-container product-grid-container--skeleton ${showFullWidth ? 'full-width' : ''}`}>
        <div className="product-grid-header">
          <div className="product-grid-skeleton-title" />
          <div className="product-grid-skeleton-sort" />
        </div>
        <div className="product-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="product-grid-card-skeleton">
              <div className="product-grid-card-skeleton-image" />
              <div className="product-grid-card-skeleton-line product-grid-card-skeleton-line--wide" />
              <div className="product-grid-card-skeleton-line" />
              <div className="product-grid-card-skeleton-line product-grid-card-skeleton-line--narrow" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (serverFilterActive && (serverFiltering || serverDataPending)) {
    return (
      <div className={`product-grid-container ${showFullWidth ? 'full-width' : ''}`}>
        <div className="loading">Filtering products...</div>
      </div>
    );
  }

  // Global catalog error — but category/search server fetches are independent.
  const serverCatalogResolved =
    serverFilterActive && Array.isArray(serverFilteredProducts) && !serverFiltering;
  if (error && products.length === 0 && !serverCatalogResolved) {
    return (
      <div className={`product-grid-container ${showFullWidth ? 'full-width' : ''}`}>
        <div className="error-products" style={{ padding: '40px', textAlign: 'center' }}>
          <h3>⚠️ Unable to Load Products</h3>
          <p>{sanitizeUserFacingError(error)}</p>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
            Please refresh the page or try again later.
          </p>
        </div>
      </div>
    );
  }

  const sortControls = (
    <div className="sort-controls">
      <label htmlFor="sort-select" className="sort-label">Sort by:</label>
      <select
        id="sort-select"
        className="sort-select"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
      >
        <option value="popular">Most Popular</option>
        <option value="price-low">Price: Low to High</option>
        <option value="price-high">Price: High to Low</option>
        <option value="discount">Highest Discount</option>
      </select>
    </div>
  );

  return (
    <div className={`product-grid-container ${showFullWidth ? 'full-width' : ''} ${layoutVariant === 'section' ? 'layout-variant-section' : ''}`}>
      {hideHeaderTitle ? (
        <div className="product-grid-header product-grid-header-minimal">
          {sortControls}
        </div>
      ) : (
      <div className="product-grid-header">
        <div className="products-title-wrapper">
          <h2 className="products-title">
            {filters.showGeek
              ? 'Raheasy Geek - Tech & DIY Products'
              : filters.showPromotions
              ? 'Black November - All Promotions'
              : filters.imageSearch 
              ? 'Similar Products' 
              : filters.searchQuery 
              ? `Search Results for "${filters.searchQuery}"` 
              : filters.subCategory && filters.subCategory !== 'all'
              ? 'Subcategory Products'
              : 'Products'}
          </h2>
          {(filters.searchQuery || filters.category !== 'all' || filters.subCategory !== 'all' || filters.store !== 'all' || filters.brand !== 'all' || filters.priceRange !== 'all' || filters.imageSearch || filters.showPromotions || filters.showGeek || filters.showPriceMatchOnly) && (
            <button 
              className="clear-filters-btn"
              onClick={() => onFilterChange({ 
                searchQuery: '', 
                category: 'all', 
                subCategory: 'all',
                store: 'all',
                brand: 'all',
                priceRange: 'all',
                priceMin: 0,
                priceMax: null,
                imageSearch: null,
                showPromotions: false,
                showGeek: false,
                showPriceMatchOnly: false,
              })}
            >
              Clear Filters
            </button>
          )}
        </div>
        {sortControls}
      </div>
      )}

      {!filters.imageSearch && showPriceMatch && priceMatchCount > 0 ? (
        <PriceMatchChipBar
          active={Boolean(filters.showPriceMatchOnly)}
          count={priceMatchCount}
          onToggle={handlePriceMatchChipToggle}
        />
      ) : null}

      {filters.imageSearch ? null : groupedDisplayProducts.length === 0 ? (
        <div className="no-products">
          <p>No products found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="product-grid">
            {displayedProducts.map((product) => (
              <ProductCard 
                key={product.offerGroup?.key || product.id || product._id} 
                product={product} 
                onAddToList={handleAddToList}
                onAddToCompare={onAddToCompare}
                onCheckAvailability={onCheckAvailability}
                onRemoveFromList={onRemoveFromList}
                listProductIds={listProductIds}
                compareProductIds={compareProductIds}
              />
            ))}
          </div>
          {hasMore && !infiniteScrollOnly && (
            <div className="show-more-container">
              <button
                type="button"
                className="show-more-btn"
                onClick={handleShowMore}
                disabled={serverLoadingMore}
              >
                {serverLoadingMore ? 'Loading…' : 'Show More Products'}
              </button>
            </div>
          )}
          {infiniteScrollOnly && (
            <InfiniteScrollLoader
              hasMore={hasMore}
              onLoadMore={handleShowMore}
              resetKey={infiniteScrollResetKey}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;

